import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Icon } from "./icon";

// Cohort spec `.input-wrap`: a flat bordered field — h-8, 10px horizontal
// padding, 1px outline border, radius r2, focus-within = primary border + a
// 2px primary/20 ring. No floating label: the label is a plain block label
// ABOVE the field (see FIELD_LABEL_CLASS), rendered when `label` is passed.
const inputVariants = cva(
  [
    "block w-full rounded-lg border border-outline bg-background type-body-m text-foreground",
    "placeholder:text-on-surface-variant/60 placeholder:font-normal",
    "transition-[border-color,box-shadow,background-color] duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none",
    "hover:border-foreground",
    "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20",
    "aria-invalid:border-error aria-invalid:hover:border-error aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/20",
    "disabled:bg-muted disabled:text-foreground/60 disabled:border-outline-variant disabled:cursor-not-allowed disabled:hover:border-outline-variant",
  ],
  {
    variants: {
      variant: {
        default: "",
        primary: "",
        secondary: "",
        info: "",
        success: "",
        warning: "",
        error: "border-error hover:border-error",
      },
      inputSize: {
        default: "h-8 px-2.5",
        sm: "h-8 px-2.5",
        lg: "h-12 px-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  },
);

// Static field-label — the Cohort spec form pattern: a plain block label
// above the field (spec uses `t-small`, fg-2). No animation, no floating.
const FIELD_LABEL_CLASS = "type-body-s text-on-surface-variant";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  startIcon?: string;
  endIcon?: string;
  label?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      startIcon,
      endIcon,
      variant,
      inputSize,
      label,
      placeholder,
      id: idProp,
      "aria-invalid": ariaInvalidProp,
      ...props
    },
    ref,
  ) => {
    const reactId = React.useId();
    const inputId = idProp !== undefined ? idProp : reactId;

    const ariaInvalid = ariaInvalidProp !== undefined
      ? ariaInvalidProp
      : variant === "error"
        ? true
        : undefined;

    const hasLabel = label !== undefined && label !== "";
    const hasStartIcon = startIcon !== undefined;
    const hasEndIcon = endIcon !== undefined;

    const inputElement = (
      <input
        ref={ref}
        id={inputId}
        type={type}
        aria-invalid={ariaInvalid}
        placeholder={placeholder}
        className={cn(
          inputVariants({ variant, inputSize }),
          hasStartIcon === true && "pl-10",
          hasEndIcon === true && "pr-10",
          className,
        )}
        {...props}
      />
    );

    // Bare input — no label, no icons.
    if (hasLabel === false && hasStartIcon === false && hasEndIcon === false) {
      return inputElement;
    }

    const fieldRow = (hasStartIcon === true || hasEndIcon === true)
      ? (
          <div className="relative w-full">
            {hasStartIcon === true && (
              <Icon
                icon={startIcon}
                className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-on-surface-variant"
              />
            )}
            {inputElement}
            {hasEndIcon === true && (
              <Icon
                icon={endIcon}
                className="pointer-events-none absolute right-3 top-1/2 z-10 -translate-y-1/2 text-on-surface-variant"
              />
            )}
          </div>
        )
      : inputElement;

    if (hasLabel === false) {
      return fieldRow;
    }

    return (
      <div className="flex w-full flex-col gap-1.5">
        <label htmlFor={inputId} className={FIELD_LABEL_CLASS}>
          {label}
        </label>
        {fieldRow}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input, FIELD_LABEL_CLASS };
